import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, X, Users, Wallet, Calendar, 
  TrendingUp, Shield, ArrowRight, Loader2, Command
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  players: Users,
  transactions: Wallet,
  employees: Shield,
  stats: TrendingUp,
};

const categoryColors = {
  players: '#00F5FF',
  transactions: '#FFD700',
  employees: '#00FF88',
  stats: '#FF6B00',
};

export function GlobalSearch({ isOpen, onClose }) {
  const { theme } = useTheme();
  const { token } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState([]);

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && results[selectedIndex]) {
        handleResultClick(results[selectedIndex]);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, results, selectedIndex, onClose]);

  // Debounced search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchDebounced = useCallback(
    debounce(async (searchQuery) => {
      if (!searchQuery.trim() || searchQuery.length < 2) {
        setResults([]);
        return;
      }
      
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/global-search?q=${encodeURIComponent(searchQuery)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setResults(data.results || []);
          setSelectedIndex(0);
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    }, 300),
    [token]
  );

  useEffect(() => {
    searchDebounced(query);
  }, [query, searchDebounced]);

  const handleResultClick = (result) => {
    // Save to recent searches
    const updated = [result, ...recentSearches.filter(r => r.id !== result.id)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
    
    // Navigate based on type
    switch (result.type) {
      case 'player':
        navigate('/players', { state: { highlightId: result.id } });
        break;
      case 'transaction':
        navigate('/battle-log', { state: { highlightId: result.id } });
        break;
      case 'employee':
        navigate('/guild-members', { state: { highlightId: result.id } });
        break;
      default:
        break;
    }
    
    onClose();
    setQuery('');
  };

  const getCategoryIcon = (type) => {
    const Icon = categoryIcons[type + 's'] || Search;
    return Icon;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]"
        onClick={onClose}
      >
        {/* Backdrop */}
        <div className={cn(
          "absolute inset-0 backdrop-blur-md",
          theme === 'dark' ? "bg-black/70" : "bg-white/70"
        )} />
        
        {/* Search Modal */}
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "relative w-full max-w-2xl mx-4 rounded-2xl border overflow-hidden shadow-2xl",
            theme === 'dark' 
              ? "bg-[#0d0d15] border-white/10" 
              : "bg-white border-gray-200"
          )}
          data-testid="global-search-modal"
        >
          {/* Search Input */}
          <div className={cn(
            "flex items-center gap-3 p-4 border-b",
            theme === 'dark' ? "border-white/10" : "border-gray-200"
          )}>
            {loading ? (
              <Loader2 className="w-5 h-5 text-[#00F5FF] animate-spin" />
            ) : (
              <Search className={cn(
                "w-5 h-5",
                theme === 'dark' ? "text-gray-400" : "text-gray-500"
              )} />
            )}
            <input
              ref={inputRef}
              type="text"
              placeholder="Search players, transactions, employees..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={cn(
                "flex-1 bg-transparent text-lg outline-none",
                theme === 'dark' 
                  ? "text-white placeholder:text-gray-500" 
                  : "text-gray-900 placeholder:text-gray-400"
              )}
              data-testid="global-search-input"
            />
            <div className="flex items-center gap-2">
              <kbd className={cn(
                "px-2 py-1 rounded text-xs font-medium",
                theme === 'dark' 
                  ? "bg-white/5 text-gray-400" 
                  : "bg-gray-100 text-gray-500"
              )}>ESC</kbd>
              <button 
                onClick={onClose}
                className={cn(
                  "p-1.5 rounded-lg transition-colors",
                  theme === 'dark' ? "hover:bg-white/5" : "hover:bg-gray-100"
                )}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Results */}
          <div className="max-h-[50vh] overflow-y-auto">
            {/* Recent Searches */}
            {!query && recentSearches.length > 0 && (
              <div className="p-3">
                <p className={cn(
                  "text-xs uppercase tracking-wider mb-2 px-2",
                  theme === 'dark' ? "text-gray-500" : "text-gray-400"
                )}>Recent Searches</p>
                {recentSearches.map((result, idx) => {
                  const Icon = getCategoryIcon(result.type);
                  return (
                    <button
                      key={`recent-${idx}`}
                      onClick={() => handleResultClick(result)}
                      className={cn(
                        "w-full flex items-center gap-3 p-3 rounded-xl transition-all",
                        theme === 'dark' ? "hover:bg-white/5" : "hover:bg-gray-50"
                      )}
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ 
                          backgroundColor: `${categoryColors[result.type + 's']}15`,
                          color: categoryColors[result.type + 's']
                        }}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className={theme === 'dark' ? "text-white" : "text-gray-900"}>
                          {result.title}
                        </p>
                        <p className={cn(
                          "text-sm",
                          theme === 'dark' ? "text-gray-500" : "text-gray-400"
                        )}>{result.subtitle}</p>
                      </div>
                      <Calendar className="w-4 h-4 text-gray-500" />
                    </button>
                  );
                })}
              </div>
            )}
            
            {/* Search Results */}
            {query && results.length > 0 && (
              <div className="p-3">
                {results.map((result, idx) => {
                  const Icon = getCategoryIcon(result.type);
                  const isSelected = idx === selectedIndex;
                  
                  return (
                    <motion.button
                      key={result.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      onClick={() => handleResultClick(result)}
                      className={cn(
                        "w-full flex items-center gap-3 p-3 rounded-xl transition-all",
                        isSelected 
                          ? theme === 'dark' 
                            ? "bg-gradient-to-r from-[#9945FF]/20 to-[#00F5FF]/20 border border-[#00F5FF]/30"
                            : "bg-gradient-to-r from-[#9945FF]/10 to-[#00F5FF]/10 border border-[#00F5FF]/40"
                          : theme === 'dark' 
                          ? "hover:bg-white/5 border border-transparent"
                          : "hover:bg-gray-50 border border-transparent"
                      )}
                      data-testid={`search-result-${idx}`}
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ 
                          backgroundColor: `${categoryColors[result.type + 's']}15`,
                          color: categoryColors[result.type + 's']
                        }}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className={cn(
                          "font-medium",
                          theme === 'dark' ? "text-white" : "text-gray-900"
                        )}>
                          {highlightMatch(result.title, query)}
                        </p>
                        <p className={cn(
                          "text-sm",
                          theme === 'dark' ? "text-gray-500" : "text-gray-400"
                        )}>{result.subtitle}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          "text-xs px-2 py-1 rounded-full",
                          theme === 'dark' ? "bg-white/5" : "bg-gray-100"
                        )} style={{ color: categoryColors[result.type + 's'] }}>
                          {result.type}
                        </span>
                        <ArrowRight className="w-4 h-4 text-gray-500" />
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            )}
            
            {/* No Results */}
            {query && !loading && results.length === 0 && (
              <div className="p-12 text-center">
                <Search className={cn(
                  "w-12 h-12 mx-auto mb-4",
                  theme === 'dark' ? "text-gray-600" : "text-gray-300"
                )} />
                <p className={theme === 'dark' ? "text-gray-400" : "text-gray-500"}>
                  No results found for "{query}"
                </p>
                <p className={cn(
                  "text-sm mt-1",
                  theme === 'dark' ? "text-gray-600" : "text-gray-400"
                )}>
                  Try searching for players, transactions, or messages
                </p>
              </div>
            )}
            
            {/* Quick Actions */}
            {!query && (
              <div className="p-3 border-t border-white/5">
                <p className={cn(
                  "text-xs uppercase tracking-wider mb-2 px-2",
                  theme === 'dark' ? "text-gray-500" : "text-gray-400"
                )}>Quick Actions</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'All Players', icon: Users, path: '/players', color: '#00F5FF' },
                    { label: 'Battle Log', icon: Wallet, path: '/battle-log', color: '#FFD700' },
                    { label: 'Team Intel', icon: Shield, path: '/employee-intel', color: '#00FF88' },
                  ].map((action) => (
                    <button
                      key={action.path}
                      onClick={() => { navigate(action.path); onClose(); }}
                      className={cn(
                        "flex items-center gap-2 p-3 rounded-xl transition-all",
                        theme === 'dark' ? "hover:bg-white/5" : "hover:bg-gray-50"
                      )}
                    >
                      <action.icon className="w-4 h-4" style={{ color: action.color }} />
                      <span className={cn(
                        "text-sm",
                        theme === 'dark' ? "text-gray-300" : "text-gray-600"
                      )}>{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className={cn(
            "flex items-center justify-between p-3 border-t text-xs",
            theme === 'dark' ? "border-white/5 text-gray-500" : "border-gray-200 text-gray-400"
          )}>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className={cn(
                  "px-1.5 py-0.5 rounded",
                  theme === 'dark' ? "bg-white/5" : "bg-gray-100"
                )}>↑</kbd>
                <kbd className={cn(
                  "px-1.5 py-0.5 rounded",
                  theme === 'dark' ? "bg-white/5" : "bg-gray-100"
                )}>↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className={cn(
                  "px-1.5 py-0.5 rounded",
                  theme === 'dark' ? "bg-white/5" : "bg-gray-100"
                )}>Enter</kbd>
                Select
              </span>
            </div>
            <span className="flex items-center gap-1">
              <Command className="w-3 h-3" />
              <span>+ K to open</span>
            </span>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Highlight matching text
function highlightMatch(text, query) {
  if (!query) return text;
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return parts.map((part, idx) => 
    part.toLowerCase() === query.toLowerCase() ? (
      <span key={idx} className="text-[#00F5FF] font-semibold">{part}</span>
    ) : part
  );
}

// Debounce utility
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Global Search Trigger Button
export function SearchTrigger({ onClick }) {
  const { theme } = useTheme();
  
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all",
        theme === 'dark' 
          ? "bg-[#1a1a2e]/50 border-white/10 hover:border-[#00F5FF]/30"
          : "bg-white border-gray-200 hover:border-cyan-300 shadow-sm"
      )}
      data-testid="search-trigger"
    >
      <Search className={cn(
        "w-4 h-4",
        theme === 'dark' ? "text-gray-400" : "text-gray-500"
      )} />
      <span className={cn(
        "text-sm",
        theme === 'dark' ? "text-gray-500" : "text-gray-400"
      )}>Search everything...</span>
      <kbd className={cn(
        "ml-auto px-2 py-0.5 rounded text-xs",
        theme === 'dark' ? "bg-white/5 text-gray-500" : "bg-gray-100 text-gray-400"
      )}>⌘K</kbd>
    </motion.button>
  );
}

export default GlobalSearch;
